import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import WaitlistForm from "@/components/WaitlistForm";
import heroImage from "@/assets/hero-waitlist.jpg";
import { Sparkles, Heart, Shield, ArrowLeft } from "lucide-react";
import SkipToContent from "@/components/SkipToContent";

const WaitlistPage = () => {
  return (
    <>
      <Helmet>
        <title>רשימת המתנה | אמון בזוגיות</title>
        <meta
          name="description"
          content="הצטרפו לרשימת ההמתנה של אמון בזוגיות - שירות שידוכים פרימיום שמשלב טכנולוגיה חכמה עם ליווי אישי"
        />
      </Helmet>
      <SkipToContent />
      <main id="main-content" role="main" tabIndex={-1} className="min-h-screen relative overflow-hidden">
        {/* Background Image with Overlay */}
        <div
          className="absolute inset-0 bg-cover bg-[center_top] md:bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${heroImage})` }}
          role="img"
          aria-hidden="true"
        >
          <div className="absolute inset-0 bg-gradient-to-b from-navy/80 via-navy/60 to-navy/90" />
        </div>

        {/* Content */}
        <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-4 py-16">
          <div className="max-w-2xl w-full text-center space-y-12">
            {/* Hero Section */}
            <div className="space-y-6 animate-fade-up">
              <p className="text-lg md:text-xl text-gold mb-2">
                התייאשת מהדייטים באפליקציות – זה המקום שלך
              </p>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-cream leading-tight">
                משהו אחר עומד להתחיל.
                <br />
                <span className="text-gold">והפעם, זה אמיתי.</span>
              </h1>
              <p className="text-xl md:text-2xl text-cream/90">
                המאגר האקסקלוסיבי של "אמון בזוגיות" נפתח בקרוב...
              </p>
            </div>

            {/* Aura of Quality */}
            <div className="space-y-4 animate-fade-up animation-delay-200">
              <div className="flex items-center justify-center gap-2">
                <Shield className="w-6 h-6 text-gold" aria-hidden="true" />
                <h2 className="text-2xl font-bold text-cream">לא לכל אחד.</h2>
              </div>
              <p className="text-cream/80 text-lg max-w-lg mx-auto">
                אנחנו בונים קהילה סגורה של אנשים רציניים בלבד.
                <br />
                כאלה שמבינים שזוגיות איכותית דורשת השקעה אמיתית.
              </p>
            </div>

            {/* Tech Mention */}
            <div className="space-y-4 animate-fade-up animation-delay-400">
              <div className="flex items-center justify-center gap-2">
                <Sparkles className="w-6 h-6 text-gold" aria-hidden="true" />
                <h2 className="text-2xl font-bold text-cream">מדע פוגש נשמה.</h2>
              </div>
              <p className="text-cream/80 text-lg max-w-lg mx-auto">
                טכנולוגיית התאמה חכמה בשילוב ליווי אנושי צמוד.
                <br />
                לא אפליקציה. לא הימור. תהליך מדויק.
              </p>
            </div>

            {/* Love Badge */}
            <div className="flex justify-center animate-fade-up animation-delay-400">
              <div className="flex items-center gap-2 bg-cream/10 backdrop-blur-sm px-6 py-3 rounded-full border border-cream/20">
                <Heart className="w-5 h-5 text-gold fill-gold" aria-hidden="true" />
                <span className="text-cream font-medium">גישה מוקדמת למוזמנים בלבד</span>
              </div>
            </div>

            {/* Quiz Links */}
            <div className="animate-fade-up animation-delay-600 space-y-4">
              <p className="text-cream/80 text-lg">גלי את ה-DNA הזוגי שלך</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/quiz-men"
                  aria-label="מעבר לקוויז התאמה לגברים"
                  className="group flex items-center justify-center gap-2 bg-gold/20 hover:bg-gold/30 border border-gold/40 text-cream px-6 py-3 rounded-full transition-all duration-300"
                >
                  <span>קוויז לגברים</span>
                  <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" aria-hidden="true" />
                </Link>
                <Link
                  to="/quiz-women"
                  aria-label="מעבר לקוויז התאמה לנשים"
                  className="group flex items-center justify-center gap-2 bg-gold/20 hover:bg-gold/30 border border-gold/40 text-cream px-6 py-3 rounded-full transition-all duration-300"
                >
                  <span>קוויז לנשים</span>
                  <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" aria-hidden="true" />
                </Link>
              </div>
            </div>

            {/* Waitlist Form */}
            <div className="animate-fade-up animation-delay-800">
              <WaitlistForm />
            </div>
          </div>
        </div>
      </main>
      
      {/* Footer */}
      <footer role="contentinfo" className="bg-navy py-4 text-center text-cream/80 text-sm">
        <div className="flex items-center justify-center gap-4 flex-wrap">
          <p>© 2024 אמון בזוגיות. כל הזכויות שמורות.</p>
          <Link to="/accessibility" className="text-gold hover:text-gold-light underline">
            הצהרת נגישות
          </Link>
        </div>
      </footer>
    </>
  );
};

export default WaitlistPage;